<template>
    <div class="discoverPost">
        <!-- Admin Controls -->
        <i
            class="fa fa-ellipsis-v discoverPost__controlsToggle"
            v-if="userIsAdmin"
            @click="controlsShowing = !controlsShowing"
        ></i>
        <div
            v-if="userIsAdmin && controlsShowing"
            class="discoverPost__admin-controls"
        >
            <p
                class="editControl"
                @click="$emit('edit-post')"
            >
                Edit
            </p>
            <p
                class="deleteControl"
                @click="onDeletePost()"
            >
                Delete
            </p>
        </div>

        <!-- Post Heading -->
        <div class="discoverPost__heading">
            <img
                class="avatar"
                :src="
                    buildAvatarUrl(
                        details.employee.email,
                        '100',
                        'ShortcutIcon'
                    )"
            />
            <h2>{{ details.employee.fullName }}</h2>
            <span class="timeStamp">{{ timestamp }}</span>
        </div>

        <!-- Post Title -->
        <h3 :class="[
                'discoverPost__title',
                {
                    idea: details.type == 'idea',
                    announcement: details.type == 'announcement',
                    poll: details.type == 'poll',
                },
            ]">
            {{ details.title }}
        </h3>

        <!-- Post Body -->
        <div class="discoverPost__body">
            <!-- Copy -->
            <p v-html="details.text"></p>

            <!-- Announcement Image -->
            <div
                v-if="details.image !== null"
                class="image-container"
            >
                <img :src="details.image" />
            </div>

            <!-- Poll Content -->
            <div
                v-if="details.poll !== null"
                class="poll"
            >
                <div
                    v-for="option in localPollOptions"
                    :key="option.id"
                    class="pollOption"
                >
                    <div
                        :class="['voteBox', { voted: option.votes.includes(activeUser.employeeNumber) }]"
                        @click="updatePollVote(option)"
                    >
                        <img
                            v-if="option.image !== null && option.image !== ''"
                            :src="option.image"
                            alt="Poll image"
                        />
                        <caption>{{ option.name }}</caption>
                    </div>
                    <p class="voteShare">{{ calculateVoteShare(option.votes) }}%</p>
                </div>
            </div>
        </div>

        <!-- Post Footer -->
        <div class="discoverPost__footer">
            <span
                v-if="details.type === 'poll'"
                class="pollNotif"
                :class="{ pollClosed: !pollIsOpen}"
            >{{ pollIsOpen ? `Closes ${formatDate(details.poll.closeDatetime, 'MM-DD-YYYY h:mm:ssa')}` : 'Poll has closed' }}</span>
            <ul class="statList">
                <li
                    class="likes-container"
                    @click="onLikeClicked()"
                >
                    <i :class="['fas', 'fa-heart', 'likeIcon', {liked: isLikedByUser }]"></i>
                    {{ details.likes.length }} likes
                </li>
                <li
                    v-if="!details.disableComments"
                    @click="showComments = !showComments"
                >{{ commentCount }}</li>
            </ul>
        </div>

        <!-- Comment Box -->
        <div
            v-if="showComments"
            class="discoverPost__commentBox"
        >
            <transition-group
                name="commentShift"
                tag="span"
                appear
            >
                <app-comment
                    v-for="comment in details.comments"
                    :key="comment.id"
                    :comment="comment"
                />
            </transition-group>
            <div class="input">
                <textarea
                    v-focus
                    rows="10"
                    v-model="newComment.comment"
                    placeholder="Enter comment..."
                    @keyup.enter="onCommentSubmit"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import { avatars, format } from "@/mixins";
import moment from "moment";

export default {
    name: "DiscoverPost",
    mixins: [avatars, format],
    components: {
        "app-comment": () => import("@/components/Shared/AppComment.vue"),
    },

    props: {
        details: Object,
        activeUser: Object,
    },

    data() {
        return {
            showComments: false,
            newComment: {
                id: null,
                employeeName: null,
                employeeAvatar: null,
                createdDatetime: null,
                comment: "",
            },
            timestamp: "",
            localPollOptions: [],
            voteShareKey: 0,
            controlsShowing: false,
        };
    },

    computed: {
        timeSince() {
            return moment(this.details.createdDatetime).fromNow();
        },

        commentCount() {
            if (this.details.comments != []) {
                const count = this.details.comments.length;
                return count == 1 ? "1 comment" : `${count} comments`;
            }
            return "0 comments";
        },

        isLikedByUser() {
            return this.details.likes.includes(this.activeUser.employeeNumber);
        },

        userIsAdmin() {
            try {
                return this.activeUser.userRoles.includes(
                    "ROLE_MARKETING_EMPLOYEE"
                );
            } catch (error) {
                /* shhhh */
            }
            return false;
        },

        pollIsOpen() {
            return moment(this.details.poll.closeDatetime).isSameOrAfter(
                moment()
            );
        },
    },

    watch: {
        details: {
            deep: true,
            handler(newValue) {
                if (newValue.poll) {
                    this.localPollOptions = JSON.parse(
                        JSON.stringify([...newValue.poll.options])
                    );
                }
            },
        },
    },

    methods: {
        ...mapActions([
            "addCommentToPost",
            "toggleEmployeeLike",
            "deletePost",
            "updatePollVotes",
        ]),

        calculateVoteShare(votes) {
            const totalVotes = this.localPollOptions
                .map((option) => option.votes)
                .reduce((sum, array) => sum + array.length, 0);
            const voteShare =
                votes.length > 0
                    ? ((votes.length / totalVotes) * 100).toFixed(2)
                    : "0.00";
            return voteShare;
        },

        onCommentSubmit() {
            const newComment = {
                id: Math.round(Math.random() * 1000),
                employee: {
                    employeeNumber: this.activeUser.employeeNumber,
                    fullName: this.activeUser.fullName,
                    email: this.activeUser.email,
                },
                filepath: null,
                comment: this.newComment.comment,
                createdDatetime: moment().toISOString(),
            };
            this.addCommentToPost({
                postId: this.details.id,
                employeeNumber: this.activeUser.employeeNumber,
                comment: newComment,
            });
            this.newComment.comment = "";
            if (this.$socket) {
                this.$socket.emit("addDiscoverPostComment", {
                    postId: this.details.id,
                    comment: newComment,
                });
            }
        },

        onLikeClicked() {
            this.toggleEmployeeLike({
                postId: this.details.id,
                employeeNumber: this.activeUser.employeeNumber,
            }).then((success) => {
                if (success) {
                    if (this.$socket) {
                        this.$socket.emit("likeDiscoverPost", {
                            postId: this.details.id,
                            employeeNumber: this.activeUser.employeeNumber,
                        });
                    }
                }
            });
        },

        onDeletePost() {
            this.deletePost(this.details.id);
            if (this.$socket) {
                this.$socket.emit("deleteDiscoverPost", this.details.id);
            }
        },

        updatePollVote(option) {
            if (!option.votes.includes(this.activeUser.employeeNumber)) {
                if (this.pollIsOpen) {
                    // remove user's previous vote
                    this.localPollOptions.forEach((option) => {
                        if (
                            option.votes.includes(
                                this.activeUser.employeeNumber
                            )
                        ) {
                            option.votes.splice(
                                option.votes.indexOf(
                                    this.activeUser.employeeNumber
                                ),
                                1
                            );
                        }
                    });
                    // add user vote to the selected option
                    option.votes.push(this.activeUser.employeeNumber);
                    // post changes
                    // this.$emit("update-vote", this.localPollOptions);
                    this.updatePollVotes({
                        postId: this.details.id,
                        pollOptions: this.localPollOptions,
                        pollOption: option,
                        employeeNumber: this.activeUser.employeeNumber,
                    }).then((success) => {
                        if (success) {
                            // update any connected clients immediately
                            if (this.$socket) {
                                this.$socket.emit("voteDiscoverPoll", {
                                    postId: this.details.id,
                                    pollOptions: this.localPollOptions,
                                });
                            }
                        }
                    });
                } else {
                    // show poll closed notification
                    console.log("poll is closed");
                }
            }
        },
    },

    mounted() {
        if (this.details.poll !== null) {
            this.localPollOptions = JSON.parse(
                JSON.stringify([...this.details?.poll?.options])
            );
        }
        this.timestamp = moment(this.details.createdDatetime).fromNow();
        // update the comment timestamp every minute
        setInterval(
            () =>
                (this.timestamp = moment(
                    this.details.createdDatetime
                ).fromNow()),
            60000
        );
    },
};
</script>

<style lang="scss" scoped>
.discoverPost {
    position: relative;
    width: 100%;
    margin: 0 0 6rem;
    padding: 1rem 1.5rem 1.5rem;
    box-shadow: $containerShadow;
    &__controlsToggle {
        position: absolute;
        top: 0.5rem;
        right: 2rem;
        padding: 1rem;
        border-radius: 20px;
        &:hover {
            background-color: rgb(223, 223, 223);
        }
    }
    &__admin-controls {
        position: absolute;
        top: 4.5rem;
        right: 0rem;
        background-color: #fff;
        padding: 1rem;
        box-shadow: $containerShadow;
        @include breakpoint(ipadPro) {
            display: none;
        }
        p {
            font-weight: 700;
            font-size: 1.4rem;
            cursor: pointer;
            &:not(:last-child) {
                margin-bottom: 0.5rem;
            }
        }
        .editControl {
            color: $teal-light;
        }
        .deleteControl {
            color: $red;
        }
    }

    &__heading {
        display: flex;
        align-items: center;
        img {
            height: 35px;
        }
        h2 {
            margin: 0 1.5rem 0 1rem;
            font-size: 1.6rem;
        }
        .timeStamp {
            font-size: 1.2rem;
        }
    }

    &__title {
        margin-top: 1rem;
        font-size: 1.6rem;
        &.idea {
            color: $teal-light;
        }
        &.announcement {
            color: $orange;
        }
        &.poll {
            color: $blue-green;
        }
    }

    &__body {
        margin: 1rem 0 0;
        font-size: 1.4rem;

        p {
            line-height: 1.7;
            word-wrap: break-word;
        }
        .image-container {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            align-items: center;
            margin: 2rem 0 1rem;
            img {
                max-width: 100%;
            }
        }
        .poll {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            .pollOption {
                margin: 1rem 2rem;
                text-align: center;
                cursor: pointer;
                .voteBox {
                    padding: 0.5rem;
                    &.voted {
                        border: 1px solid $teal-light;
                    }
                }

                img {
                    max-width: 140px;
                    height: auto;
                }
                caption {
                    display: block;
                }
            }
        }
    }

    &__footer {
        margin: 2rem 0 0;
        display: flex;
        align-items: center;
        .pollNotif {
            font-size: 1.1rem;
            &.pollClosed {
                background-color: $red;
                color: #fff;
                padding: 3px;
            }
        }
        .statList {
            display: flex;
            justify-content: space-around;
            margin-left: auto;
            li {
                margin-left: 3rem;
                font-size: 1.4rem;
                color: $gray-dark;
                cursor: pointer;
                &.likes-container {
                    .likeIcon {
                        margin-right: 0.5rem;
                        font-size: 1.2rem;
                        &.liked {
                            color: $red;
                        }
                    }
                }
            }
        }
    }

    &__commentBox {
        margin-top: 2rem;
        .input {
            margin-top: 2rem;

            textarea {
                width: 100%;
                height: 6rem;
                padding: 1rem;
                font-family: "Nunito Sans", sans-serif;
                border: 1px solid $gray;
                border-radius: 5px;
                outline: none;
                resize: none;
            }
        }
    }
}

// TRANSITIONS
.commentShift-enter-active,
.commentShift-leave-active {
    transition: transform 0.3s ease-in-out, opacity 0.5s ease-in-out;
    transform-origin: center;
}
.commentShift-enter,
.commentShift-leave-to {
    opacity: 0;
    transform: translateY(2rem);
}
</style>
